import { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Link, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Loading } from 'framework/ui/mui-builder';
import {
  DEFINITION_CATEGORY_LABELS,
  GAP_DETAILS,
  GAP_GROUPS,
  MIXED_CATEGORY,
  MIXED_CATEGORY_LABEL,
} from '../../constants';
import { useCoreServiceClient } from '../../api/coreServiceClient';
import useAsyncData from '../../hooks/useAsyncData';
import EmptyState from '../../components/EmptyState';
import ErrorNotice from '../../components/ErrorNotice';
import FilterChip from '../../components/FilterChip';
import StatTile from '../../components/StatTile';
import TextDialog from '../../components/TextDialog';
import type { DefinitionSummaryDto, FamilySummaryDto, InventoryDto } from '../../types';
import { formatNumber } from '../../utils/format';
import FamilyCard, { StateBadge } from './FamilyCard';
import { getGapGroup } from './GapTag';

type SortKey = 'tickets' | 'open' | 'variants' | 'states' | 'name';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'tickets', label: 'Sort: most tickets' },
  { value: 'open', label: 'Most open tickets' },
  { value: 'variants', label: 'Most variants' },
  { value: 'states', label: 'Most states' },
  { value: 'name', label: 'Name' },
];

const SORTERS: Record<SortKey, (a: FamilySummaryDto, b: FamilySummaryDto) => number> = {
  tickets: (a, b) => b.instances - a.instances,
  open: (a, b) => b.openInstances - a.openInstances,
  variants: (a, b) => b.definitions.length - a.definitions.length || b.instances - a.instances,
  states: (a, b) => b.states.length - a.states.length,
  name: (a, b) => a.definitions[0].name.localeCompare(b.definitions[0].name),
};

type DocumentRequest = { title: string; fileName: string; load: () => Promise<string> };

const getTotals = (inventory: InventoryDto) => ({
  tickets: inventory.familyList.reduce((sum, family) => sum + family.instances, 0),
  open: inventory.familyList.reduce((sum, family) => sum + family.openInstances, 0),
});

const BlueprintsTab = () => {
  const client = useCoreServiceClient();
  const inventory = useAsyncData(() => client.getInventory(), [client]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [gap, setGap] = useState('');
  const [sort, setSort] = useState<SortKey>('tickets');
  const [isUsedOnly, setUsedOnly] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [textDocument, setTextDocument] = useState<Nullable<DocumentRequest>>(null);

  const data = inventory.data;

  const categories = useMemo(
    () => [...new Set((data?.familyList ?? []).map(family => family.category || MIXED_CATEGORY))],
    [data],
  );

  const families = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return (data?.familyList ?? [])
      .filter(
        family =>
          (!category || (family.category || MIXED_CATEGORY) === category) &&
          (!isUsedOnly || family.instances > 0) &&
          (!gap || family.definitions.some(definition => definition.gaps.includes(gap))) &&
          (!needle ||
            family.definitions.some(definition => definition.name.toLowerCase().includes(needle)) ||
            family.states.some(state => state.toLowerCase().includes(needle))),
      )
      .sort(SORTERS[sort]);
  }, [data, query, category, gap, isUsedOnly, sort]);

  const openDefinitionYaml = (definition: DefinitionSummaryDto) =>
    setTextDocument({
      title: definition.name,
      fileName: `${definition.id}.yaml`,
      load: () => client.getDefinitionYaml(definition.id),
    });

  if (inventory.error && !data) {
    return <ErrorNotice error={inventory.error} onRetry={inventory.reload} />;
  }

  if (!data) {
    return <Loading isInitialPosition />;
  }

  const { tickets, open } = getTotals(data);
  const gapCounts = data.gapsByCode;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        sx={{ marginBottom: 1 }}
      >
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h5" sx={{ marginBottom: 0.5 }}>
            Blueprints
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A blueprint is a state machine definition: one per Redmine tracker, plus the built-in organization and
            business function. Trackers with the same states and the same transitions between them form a <b>family</b>{' '}
            — variants of one blueprint.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} sx={{ whiteSpace: 'nowrap' }}>
          <Link
            component="button"
            type="button"
            onClick={() =>
              setTextDocument({
                title: 'definitions.yaml',
                fileName: 'definitions.yaml',
                load: client.getDefinitionsYaml,
              })
            }
          >
            definitions.yaml
          </Link>
          <span>·</span>
          <Link
            component="button"
            type="button"
            onClick={() =>
              setTextDocument({
                title: 'JSON schema',
                fileName: 'definition.schema.json',
                load: client.getDefinitionSchema,
              })
            }
          >
            JSON schema
          </Link>
        </Stack>
      </Stack>

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ marginBottom: 2 }}>
        <StatTile value={formatNumber(data.families)} label="families (unique shapes)" />
        <StatTile value={formatNumber(data.definitions)} label="definitions" />
        <StatTile value={formatNumber(data.usedDefinitions)} label="in use" />
        <StatTile value={formatNumber(data.definitions - data.usedDefinitions)} label="without tickets" />
        <StatTile value={formatNumber(tickets)} label="tickets" />
        <StatTile value={formatNumber(open)} label="open tickets" />
      </Stack>

      {data.overlayProblems.length ? (
        <Paper variant="outlined" sx={{ padding: '10px 12px', marginBottom: 1.5, borderColor: 'warning.main' }}>
          <Typography variant="subtitle2" color="warning.main">
            Overlay problems
          </Typography>
          {data.overlayProblems.map(problem => (
            <Typography key={`${problem.file}:${problem.message}`} variant="body2">
              {problem.file}: {problem.message}
            </Typography>
          ))}
        </Paper>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 1.25,
          marginBottom: 2,
        }}
      >
        {GAP_GROUPS.map(group => {
          const codes = Object.keys(gapCounts)
            .filter(code => getGapGroup(code) === group.id)
            .sort((a, b) => gapCounts[b] - gapCounts[a]);

          if (!codes.length) {
            return null;
          }

          return (
            <Paper key={group.id} variant="outlined" sx={{ padding: '10px 12px' }}>
              <Typography
                variant="subtitle2"
                sx={{ color: group.id === 'structure' ? 'warning.main' : 'text.primary' }}
              >
                {group.title}
              </Typography>
              {group.hint ? (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ marginBottom: 1 }}>
                  {group.hint}
                </Typography>
              ) : null}
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ marginTop: 1 }}>
                {codes.map(code => (
                  <FilterChip
                    key={code}
                    label={GAP_DETAILS[code]?.label ?? code}
                    count={formatNumber(gapCounts[code])}
                    title={GAP_DETAILS[code]?.hint ?? code}
                    isActive={gap === code}
                    onClick={() => setGap(gap === code ? '' : code)}
                  />
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ marginBottom: 1.5 }}>
        <TextField
          size="small"
          type="search"
          label="Search tracker or state"
          value={query}
          onChange={event => setQuery(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          value={category}
          onChange={event => setCategory(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All categories</MenuItem>
          {categories.map(item => (
            <MenuItem key={item} value={item}>
              {DEFINITION_CATEGORY_LABELS[item] || MIXED_CATEGORY_LABEL}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={sort}
          onChange={event => setSort(event.target.value as SortKey)}
          sx={{ minWidth: 200 }}
        >
          {SORT_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          label="in use only"
          control={<Checkbox checked={isUsedOnly} onChange={event => setUsedOnly(event.target.checked)} />}
        />
        <Typography variant="body2" color="text.secondary">
          {families.length} of {data.familyList.length} families
        </Typography>
      </Stack>

      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ marginBottom: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          <StateBadge name="state" kind="TRANSIENT" /> transient — must move on, has SLA
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <StateBadge name="state" kind="STEADY" /> steady — may stay, still owned
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <StateBadge name="state" kind="TERMINAL" /> terminal — done
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <StateBadge name="state" kind="TRANSIENT" isInitial /> initial
        </Typography>
      </Stack>

      {families.length ? (
        families.map(family => (
          <FamilyCard
            key={family.family}
            family={family}
            isExpanded={!!expanded[family.family]}
            onToggle={key => setExpanded(previous => ({ ...previous, [key]: !previous[key] }))}
            onOpenYaml={openDefinitionYaml}
          />
        ))
      ) : (
        <EmptyState>No families match</EmptyState>
      )}

      {textDocument ? (
        <TextDialog
          title={textDocument.title}
          fileName={textDocument.fileName}
          load={textDocument.load}
          onClose={() => setTextDocument(null)}
        />
      ) : null}
    </Box>
  );
};

export default BlueprintsTab;
